import React, { createContext, useState, useCallback, useContext } from 'react';
import type { ReactNode } from 'react'; // Changed this line
import NotificationContainer from '../components/common/NotificationContainer';
import type { Notification, NotificationType } from '../components/common/NotificationItem';

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (message: string, type?: NotificationType, duration?: number) => void;
  dismissNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prevNotifications =>
      prevNotifications.filter(notification => notification.id !== id)
    );
  }, []);

  const addNotification = useCallback((message: string, type: NotificationType = 'info', duration: number = 4000) => {
    const id = Math.random().toString(36).substring(2, 9) + Date.now();
    setNotifications(prevNotifications => [...prevNotifications, { id, message, type }]);

    setTimeout(() => {
      dismissNotification(id);
    }, duration);
  }, [dismissNotification]);

  return (
    <NotificationContext.Provider value={{ notifications, addNotification, dismissNotification }}>
      {children}
      <NotificationContainer notifications={notifications} onDismiss={dismissNotification} />
    </NotificationContext.Provider>
  );
};